import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Form, Formik} from 'formik';
import {Redirect, Route, Switch} from 'react-router-dom';
import {find, findIndex, merge, take} from 'lodash';
import {SESSION_STORAGE_KEY, STEP_COMPONENTS, STEPS} from './constants';
import FooterWithSteps from 'shared/components/footer-with-steps';
import SurveyTypeService from 'src/services/SurveyTypeService';

export default class Survey extends Component {
  static propTypes = {
    onSubmit: PropTypes.func,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        id: PropTypes.string,
      }).isRequired,
    }).isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
  };

  componentDidMount() {
    SurveyTypeService.show(this.surveyId()).then((survey) => {
      this.setState({survey});
    });
  }

  validateAnswersForQuestion = (question, answers) => {
    const answersForQuestion = answers.filter((answer) => answer.questionId === question._id);

    switch (question.type) {
      case 'Survey::LikertQuestion':
      case 'Survey::SatisfactionQuestion':
        const answer = answersForQuestion[0] || {};

        if (!answer.content) {
          return 'You need to select a level of satisfaction.';
        }
        break;
      case 'Survey::ImpactFactorQuestion':
        if (!answersForQuestion.length) {
          return 'You must select at least one item.';
        }
        break;
    }
  };

  // if validateAnswersForQuestion returns a message, then this is invalid, if it returns no message then it is valid
  answersForQuestionInvalid = (question, answers) =>
    !!this.validateAnswersForQuestion(question, answers);

  validate = (values) => {
    const errors = {};

    this.questions().forEach((question) => {
      const error = this.validateAnswersForQuestion(question, values.answers);
      if (error) {
        errors[question._id] = error;
      }
    });

    return errors;
  };

  questions = () => this.state.survey.questions;

  surveyId = () => this.props.match.params.id;

  handleNext = (currentStep, values) => () => {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(values));
    this.props.history.push(this.stepPaths()[currentStep + 1]);
  };

  handleBack = (currentStep) => () => this.props.history.push(this.stepPaths()[currentStep - 1]);

  handleSubmit = (values) => () => {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    this.props.onSubmit(values);
  };

  shouldRedirect = (currentStep, values) => {
    const currentQuestionId = this.questionIdFromPath(this.stepPaths()[currentStep]);
    const currentQuestionIndex = findIndex(this.questions(), {_id: currentQuestionId});

    // we want all the questions before this step, since currentQuestionIndex is 0-indexed using it as n for the "take"
    // function gives us all those steps before the current one
    const questionsToValidate = take(this.questions(), currentQuestionIndex);

    return questionsToValidate.some((question) =>
      this.answersForQuestionInvalid(question, values.answers)
    );
  };

  stepPaths = () => {
    const paths = [];

    this.questions().forEach((question) => {
      STEPS[question.type](question).forEach((path) => {
        paths.push(`/survey/${this.surveyId()}/questions/${question._id}/${path}`);
      });
    });

    return paths;
  };

  initialValues = () => {
    const values = {surveyTypeId: this.surveyId(), answers: []};

    this.questions().forEach((question) => {
      if (question.type === 'Survey::ImpactFactorQuestion') {
        return;
      }

      values.answers.push({questionId: question._id, identifier: question.identifier, content: ''});
    });

    const savedValues = window.sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (savedValues) {
      const parsedSavedValues = JSON.parse(savedValues);

      return merge(values, parsedSavedValues);
    }

    return values;
  };

  questionIdFromPath = (path) => {
    const pathParts = path.split('/');
    return pathParts[pathParts.length - 2];
  };

  currentQuestion = () => {
    const question = find(this.questions(), {
      _id: this.questionIdFromPath(this.props.location.pathname),
    });
    return question || this.questions()[0];
  };

  render() {
    if (!this.state) {
      return null;
    }

    const currentStep = Math.max(this.stepPaths().indexOf(this.props.location.pathname), 0);
    const isLastStep = currentStep === this.stepPaths().length - 1;
    const isFirstStep = currentStep === 0;

    return (
      <Formik initialValues={this.initialValues()} validate={this.validate}>
        {({values, isSubmitting}) => (
          <Form onSubmit={this.handleSubmit(values)} className="survey-form-wrapper">
            <Switch>
              {this.shouldRedirect(currentStep, values) && <Redirect to={this.stepPaths()[0]} />}
              {this.stepPaths().map((path) => {
                const pathParts = path.split('/');

                const step = pathParts.pop();
                const questionId = this.questionIdFromPath(path);

                const StepComponent = STEP_COMPONENTS[step];
                const question = find(this.questions(), {_id: questionId});

                return (
                  <Route
                    key={path}
                    path={path}
                    render={(props) => <StepComponent {...props} question={question} />}
                  />
                );
              })}
              <Redirect from="*" to={this.stepPaths()[0]} />
            </Switch>
            <FooterWithSteps
              currentStepIndex={currentStep}
              numberOfSteps={this.stepPaths().length}
              onBack={isFirstStep ? null : this.handleBack(currentStep)}
              onNext={isLastStep ? this.handleSubmit(values) : this.handleNext(currentStep, values)}
              primaryText={isLastStep ? 'Submit' : 'Next'}
              primaryActionDisabled={
                isSubmitting ||
                this.answersForQuestionInvalid(this.currentQuestion(), values.answers)
              }
            />
          </Form>
        )}
      </Formik>
    );
  }
}
